// ============================================
// Path: /frontend/src/pages/SubscriptionCheckoutPage.jsx
// Purpose: Checkout page for subscription upgrades
// UPDATED: Added proration logic for upgrades
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import api from '../api/client';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, ArrowLeft, Phone } from 'lucide-react';

export default function SubscriptionCheckoutPage() {
  console.log('[SubscriptionCheckout] Component mounted');

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  
  // NEW: Proration state
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [proratedAmount, setProratedAmount] = useState(null);

  useEffect(() => {
    // Get plan from navigation state or sessionStorage
    const planFromState = location.state?.plan;
    const planFromStorage = sessionStorage.getItem('upgradePlan');

    if (planFromState) {
      console.log('[SubscriptionCheckout] Plan from navigation:', planFromState);
      setSelectedPlan(planFromState);
      sessionStorage.setItem('upgradePlan', JSON.stringify(planFromState));
    } else if (planFromStorage) {
      console.log('[SubscriptionCheckout] Plan from storage');
      setSelectedPlan(JSON.parse(planFromStorage));
    } else {
      console.log('[SubscriptionCheckout] No plan found, redirecting');
      toast({
        variant: 'destructive',
        title: 'No Plan Selected',
        description: 'Please select a plan to upgrade.',
      });
      navigate('/subscription/upgrade');
      return;
    }

    // Pre-fill phone number from user profile
    if (user?.phone) {
      setPhoneNumber(user.phone);
    }
  }, [location.state, user, navigate, toast]);

  // NEW: Fetch current subscription and calculate proration
  useEffect(() => {
    const fetchCurrentSubscription = async () => {
      if (!selectedPlan || !user) return;

      try {
        console.log('[SubscriptionCheckout] Fetching current subscription');
        const response = await api.get('/subscription/current');
        
        if (response.data.subscription) {
          const currentSub = response.data.subscription;
          setCurrentSubscription(currentSub);
          
          // Only calculate proration if user has an active paid subscription
          if (currentSub.status === 'ACTIVE' && currentSub.amountPaid && currentSub.amountPaid > 0) {
            const proration = calculateProration(currentSub, selectedPlan);
            setProratedAmount(proration);
            console.log('[SubscriptionCheckout] Proration calculated:', proration);
          }
        }
      } catch (error) {
        console.error('[SubscriptionCheckout] Error fetching subscription:', error);
        // Don't show error toast - just proceed without proration
      }
    };

    fetchCurrentSubscription();
  }, [selectedPlan, user]);

  // NEW: Calculate proration
  const calculateProration = (currentSub, newPlan) => {
    // Daily rate of current subscription
    const dailyRate = currentSub.amountPaid / 30;
    
    // Days remaining until expiry
    const today = new Date();
    const expiryDate = new Date(currentSub.expiresAt);
    const daysRemaining = Math.max(0, Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24)));
    
    // Days used
    const daysUsed = 30 - daysRemaining;
    
    // Amount used and unused
    const amountUsed = dailyRate * daysUsed;
    const amountUnused = dailyRate * daysRemaining;
    
    // New amount to charge (ensure it's not negative)
    const adjustedAmount = Math.max(0, newPlan.price - amountUnused);
    
    return {
      currentTier: currentSub.tier,
      amountPaid: currentSub.amountPaid,
      dailyRate: Math.round(dailyRate),
      daysUsed: daysUsed,
      daysRemaining: daysRemaining,
      amountUsed: Math.round(amountUsed),
      amountUnused: Math.round(amountUnused),
      newPlanPrice: newPlan.price,
      finalAmount: Math.round(adjustedAmount)
    };
  };

  const validatePhoneNumber = (phone) => {
    // Must be 254XXXXXXXXX format (Kenyan number)
    const phoneRegex = /^254[17]\d{8}$/;
    
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }
    
    if (!phoneRegex.test(phone)) {
      setPhoneError('Invalid format. Use 254XXXXXXXXX (e.g., 254712345678)');
      return false;
    }
    
    setPhoneError('');
    return true;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    setPhoneNumber(value);
    if (value) {
      validatePhoneNumber(value);
    }
  };

  const handleProceedToPayment = async () => {
    console.log('[SubscriptionCheckout] Proceeding to payment');

    if (!validatePhoneNumber(phoneNumber)) {
      return;
    }

    if (!selectedPlan) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No plan selected.',
      });
      return;
    }

    setLoading(true);

    try {
      // UPDATED: Use prorated amount if available
      const amountToCharge = proratedAmount ? proratedAmount.finalAmount : selectedPlan.price;
      
      console.log('[SubscriptionCheckout] Initiating payment:', {
        tier: selectedPlan.tier,
        amount: amountToCharge,
        phoneNumber,
        isProrated: !!proratedAmount
      });

      const response = await api.post('/subscription/upgrade', {
        tier: selectedPlan.tier,
        amount: amountToCharge,
        phoneNumber: phoneNumber,
        isProrated: !!proratedAmount,
        proratedDetails: proratedAmount
      });

      console.log('[SubscriptionCheckout] Payment initiated:', response.data);

      if (response.data.success) {
        // Store payment details for status page
        sessionStorage.setItem('paymentDetails', JSON.stringify({
          paymentId: response.data.paymentId,
          checkoutRequestId: response.data.checkoutRequestId,
          tier: selectedPlan.tier,
          amount: amountToCharge,
          phoneNumber: phoneNumber,
        }));

        toast({
          title: 'Payment Initiated',
          description: 'Please check your phone for the M-Pesa prompt.',
        });

        // Navigate to payment status page
        navigate('/subscription/payment-status', {
          state: {
            paymentId: response.data.paymentId,
            checkoutRequestId: response.data.checkoutRequestId,
            tier: selectedPlan.tier,
          },
        });
      }
    } catch (error) {
      console.error('[SubscriptionCheckout] Payment error:', error);
      toast({
        variant: 'destructive',
        title: 'Payment Failed',
        description: error.response?.data?.message || 'Failed to initiate payment. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!selectedPlan) {
    return null;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-12 md:py-20">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/subscription/upgrade')}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Plans
          </Button>

          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-bold">
                Complete Your Upgrade
              </CardTitle>
              <CardDescription>
                Review your selection and enter your M-Pesa number
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Plan Summary */}
              <div className="bg-muted p-6 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Upgrading to</p>
                    <h3 className="text-2xl font-bold flex items-center gap-2">
                      <span>{selectedPlan.icon}</span>
                      {selectedPlan.name}
                    </h3>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold">
                      KES {selectedPlan.price.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">per month</p>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <p className="text-sm font-semibold">What you'll get:</p>
                  {selectedPlan.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* NEW: Proration Breakdown */}
              {proratedAmount && proratedAmount.amountUnused > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-sm text-green-900">
                    🎉 Upgrade Credit Applied!
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Current Plan ({proratedAmount.currentTier}):
                      </span>
                      <span>KES {proratedAmount.amountPaid.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Days Used ({proratedAmount.daysUsed} days @ KES {proratedAmount.dailyRate}/day):
                      </span>
                      <span className="text-red-600">-KES {proratedAmount.amountUsed.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-green-700">
                      <span>Unused Credit ({proratedAmount.daysRemaining} days):</span>
                      <span>-KES {proratedAmount.amountUnused.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-green-300 pt-2 flex justify-between font-bold text-green-900">
                      <span>Amount to Pay:</span>
                      <span className="text-lg">KES {proratedAmount.finalAmount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Phone Number Input */}
              <div className="space-y-2">
                <Label htmlFor="phone">M-Pesa Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="254712345678"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    className={`pl-10 ${phoneError ? 'border-red-500' : ''}`}
                    maxLength={12}
                  />
                </div>
                {phoneError && (
                  <p className="text-sm text-red-500">{phoneError}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Enter the phone number registered with M-Pesa. Format: 254XXXXXXXXX
                </p>
              </div>

              {/* Important Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">Important:</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• You will receive an M-Pesa prompt on your phone</li>
                  <li>• Enter your M-Pesa PIN to complete payment</li>
                  <li>• Your upgrade will be activated immediately after payment</li>
                  <li>• Subscription is valid for 30 days from today</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter>
              <Button
                size="lg"
                className="w-full"
                onClick={handleProceedToPayment}
                disabled={loading || !phoneNumber || !!phoneError}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Pay KES {(proratedAmount?.finalAmount || selectedPlan.price).toLocaleString()}
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}